import { api, type TrainingOverview, type TrainingPerson } from '../api';
import { callout, field, input, opt, openDialog, select, todayISO, val } from '../dialog';
import { errorCard, esc, fmtDate, html, loading, pill, tile } from '../ui';

/**
 * Training & authorisations.
 *
 * On one screen because an authorisation rests on training that is still in
 * date. A certifying staff authorisation whose human factors training lapsed
 * last month is not a valid authorisation — and nothing else in the
 * application notices that on its own, so this screen says it plainly.
 */

const AUTH_SHORT: Record<string, string> = {
  ARC_ISSUE: 'ARC issue',
  ARC_RECOMMEND: 'ARC recommend',
  CRS_145: 'CRS · Part-145',
  CRS_ML801: 'CRS · ML.A.801(b)(2)',
  PILOT_OWNER: 'Pilot-owner',
  AMP_APPROVE: 'AMP approve',
};

function personCard(p: TrainingPerson, o: TrainingOverview): string {
  return html`
    <div class="card">
      <div class="card-h">
        <div>
          <h2>${esc(p.displayName)}</h2>
          <p class="tiny dim">
            ${esc(p.jobTitle ?? 'role not recorded')}${p.licence ? ` · ${esc(p.licence)}` : ''}
          </p>
        </div>
        <div style="display:flex;gap:8px;align-items:center">
          ${pill(
            p.state === 'EXPIRED' ? 'OVERDUE' : p.state === 'DUE_SOON' ? 'DUE_SOON' : 'OK',
            p.state === 'EXPIRED' ? `${p.expired} expired` : p.state === 'DUE_SOON' ? `${p.dueSoon} due soon` : 'Current',
          )}
          <button class="btn sm" data-record="${esc(p.id)}">Record training</button>
        </div>
      </div>

      ${p.authorisationsAtRisk.length
        ? html`<div class="card-b">${callout('exceed', '✕', html`
            ${esc(p.displayName)} holds ${esc(p.authorisationsAtRisk.map((a) => AUTH_SHORT[a] ?? a).join(', '))}
            but has training that has expired. An authorisation resting on lapsed training is not a valid
            authorisation — the training has to be brought up to date, or the authorisation suspended.
          `)}</div>`
        : ''}

      <div class="scrollx">
        <table class="tbl" style="min-width:0">
          <thead><tr><th>Requirement</th><th class="col-opt">Provider</th><th>Completed</th><th>Expires</th></tr></thead>
          <tbody>
            ${p.training.length
              ? p.training
                  .map(
                    (t) => html`<tr>
                      <td class="st s-${t.state === 'EXPIRED' ? 'exceed' : t.state === 'DUE_SOON' ? 'caution' : 'ok'}">
                        ${esc(o.requirements.find((r) => r.requirement === t.requirement)?.label ?? t.requirement)}
                        <span class="sub2">${esc(t.title)}</span>
                      </td>
                      <td class="col-opt tiny muted">${esc(t.provider ?? '—')}</td>
                      <td class="n">${esc(fmtDate(t.completedAt))}</td>
                      <td>
                        ${t.expiresAt
                          ? pill(
                              t.state === 'EXPIRED' ? 'OVERDUE' : t.state === 'DUE_SOON' ? 'DUE_SOON' : 'OK',
                              t.state === 'EXPIRED' ? 'Expired' : `${t.daysLeft} d`,
                            )
                          : '<span class="chip">no expiry</span>'}
                        <span class="sub2">${esc(fmtDate(t.expiresAt))}</span>
                      </td>
                    </tr>`,
                  )
                  .join('')
              : '<tr><td colspan="4" class="muted">No training recorded.</td></tr>'}
          </tbody>
        </table>
      </div>

      ${p.authorisations.length
        ? html`<div class="card-f">
            Holds ${esc(p.authorisations.map((a) => AUTH_SHORT[a.kind] ?? a.kind).join(' · '))}
          </div>`
        : ''}
    </div>
  `;
}

function view(o: TrainingOverview): string {
  // Anyone with a problem first — this screen exists to surface those.
  const order = { EXPIRED: 0, DUE_SOON: 1, OK: 2 } as Record<string, number>;
  const people = [...o.people].sort((a, b) => (order[a.state] ?? 3) - (order[b.state] ?? 3));

  return html`
    <div class="page-h">
      <div>
        <h1>Training &amp; authorisations</h1>
        <p class="dim">
          Recurrent training under Part-CAMO / Part-CAO, and the authorisations that depend on it
        </p>
      </div>
      <button class="btn pri" data-record-any>Record training</button>
    </div>

    ${o.summary.authorisationsAtRisk
      ? callout('exceed', '✕', html`
          ${o.summary.authorisationsAtRisk} authorisation(s) are held by staff whose training has expired.
          Those signatures would not stand up at an audit.
        `)
      : ''}

    <div class="tiles">
      ${tile({ label: 'Staff', value: o.summary.staff, sub: 'currently employed' })}
      ${tile({
        label: 'Training expired',
        value: o.summary.expired,
        sub: o.summary.expired ? 'needs renewing now' : 'none',
        tone: o.summary.expired ? 'exceed' : 'ok',
      })}
      ${tile({
        label: `Due within ${o.warnDays} days`,
        value: o.summary.dueSoon,
        sub: 'book the renewal',
        tone: o.summary.dueSoon ? 'caution' : 'ok',
      })}
      ${tile({ label: 'Fully current', value: o.summary.current, sub: 'nothing outstanding', tone: 'ok' })}
    </div>

    <div class="stack">${people.map((p) => personCard(p, o)).join('')}</div>
  `;
}

function recordTraining(o: TrainingOverview, personId: string | null, reload: () => void): void {
  let requirement = o.requirements[0].requirement;

  openDialog<Record<string, unknown>>({
    wide: true,
    title: 'Record training',
    sub: 'The expiry is worked out from the requirement, not typed in',
    primary: 'Record',
    body: () => {
      const spec = o.requirements.find((r) => r.requirement === requirement);
      return html`
        <div class="frow">
          ${field('Person', select('personId', o.people.map((p) => opt(p.id, `${p.displayName}${p.jobTitle ? ` — ${p.jobTitle}` : ''}`, p.id === personId)).join('')))}
          ${field('Requirement', select('requirement', o.requirements.map((r) => opt(r.requirement, r.label, r.requirement === requirement)).join(''), 'data-requirement'))}
        </div>
        ${spec ? callout('info', 'i', html`
          ${esc(spec.appliesTo)}.
          ${spec.validMonths
            ? `Valid ${spec.validMonths} months from completion — the expiry is computed, so two people who did the same course on the same day cannot end up with different dates.`
            : 'No fixed validity period; this one does not expire on its own.'}
        `) : ''}
        <div class="frow">
          ${field('Course title', input('title', spec?.label ?? '', 'maxlength="200"'))}
          ${field('Provider', input('provider', '', 'maxlength="160"'))}
        </div>
        <div class="frow">
          ${field('Completed', input('completedAt', todayISO(), 'type="date"'))}
          ${field('Assessed by', select('assessorPersonId', [opt('', 'Not assessed'), ...o.people.map((p) => opt(p.id, p.displayName))].join('')))}
        </div>
        ${field('Notes', input('notes', '', 'maxlength="4000"'))}
      `;
    },
    onRender: (root, refresh) => {
      root.querySelector<HTMLSelectElement>('[data-requirement]')?.addEventListener('change', (e) => {
        requirement = (e.target as HTMLSelectElement).value;
        refresh();
      });
    },
    collect: (root) => {
      const title = val(root, 'title');
      if (!title) return { error: 'Give the course a title.' };
      const completedAt = val(root, 'completedAt');
      if (completedAt > todayISO()) return { error: 'Training cannot be recorded as completed in the future.' };
      return {
        personId: val(root, 'personId'),
        requirement: val(root, 'requirement'),
        title,
        provider: val(root, 'provider') || undefined,
        completedAt,
        assessorPersonId: val(root, 'assessorPersonId') || undefined,
        notes: val(root, 'notes') || undefined,
      };
    },
    submit: (payload) => api.training.record(payload),
    done: (r) =>
      r.expiresAt
        ? `${r.displayName} — ${r.title}, valid to ${String(r.expiresAt).slice(0, 10)}`
        : `${r.displayName} — ${r.title} recorded`,
    onDone: reload,
  });
}

export async function renderTraining(host: HTMLElement): Promise<void> {
  host.innerHTML = loading('Loading training records');
  try {
    const o = await api.training.overview();
    host.innerHTML = view(o);
    const reload = () => void renderTraining(host);

    host.querySelector('[data-record-any]')?.addEventListener('click', () =>
      recordTraining(o, o.people[0]?.id ?? null, reload),
    );
    host.querySelectorAll<HTMLElement>('[data-record]').forEach((b) =>
      b.addEventListener('click', () => recordTraining(o, b.dataset.record!, reload)),
    );
  } catch (err) {
    host.innerHTML = errorCard(err instanceof Error ? err.message : 'Could not load training records.');
  }
}
